import { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { QRCodeSVG } from 'qrcode.react'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { useQRCodeStore } from '../../stores/qrCodeStore'

export default function QRCodePage() {
  const { qrCodes, loading, error, fetchQRCodes, createQRCode, deleteQRCode } = useQRCodeStore()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [qrToDelete, setQrToDelete] = useState<string | null>(null)
  const [expiresInHours, setExpiresInHours] = useState(24)
  const [maxUses, setMaxUses] = useState(1)
  const [newCode, setNewCode] = useState<string | null>(null)

  useEffect(() => {
    fetchQRCodes()
  }, [fetchQRCodes])

  const handleCreate = async () => {
    try {
      const code = await createQRCode({ expiresInHours, maxUses })
      setNewCode(code)
    } catch {
      // Error handled by store
    }
  }

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setNewCode(null)
    setExpiresInHours(24)
    setMaxUses(1)
  }

  const handleDeleteClick = (id: string) => {
    setQrToDelete(id)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (qrToDelete) {
      try {
        await deleteQRCode(qrToDelete)
      } catch {
        // Error handled by store
      }
    }
    setDeleteDialogOpen(false)
    setQrToDelete(null)
  }

  if (loading && qrCodes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          QR Code 管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          產生 QR Code
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {qrCodes.map((qr) => {
          const isExpired = new Date() > qr.expiresAt
          const isUsedUp = qr.currentUses >= qr.maxUses

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={qr.id}>
              <Card
                sx={{
                  opacity: isExpired || isUsedUp ? 0.6 : 1,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <QRCodeSVG
                      value={JSON.stringify({ code: qr.code, type: 'registration' })}
                      size={150}
                    />
                  </Box>

                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontFamily: 'monospace', letterSpacing: 2 }}
                    >
                      {qr.code}
                    </Typography>
                    <IconButton size="small" onClick={() => handleCopy(qr.code)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {isExpired && (
                      <Chip label="已過期" color="error" size="small" />
                    )}
                    {isUsedUp && (
                      <Chip label="已用完" color="warning" size="small" />
                    )}
                    {!isExpired && !isUsedUp && (
                      <Chip label="有效" color="success" size="small" />
                    )}
                    <Chip
                      label={`${qr.currentUses}/${qr.maxUses} 次`}
                      variant="outlined"
                      size="small"
                    />
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    建立: {format(qr.createdAt, 'MM/dd HH:mm', { locale: zhTW })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    到期: {format(qr.expiresAt, 'MM/dd HH:mm', { locale: zhTW })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    建立者: {qr.createdBy}
                  </Typography>

                  {/* Show users who used this QR code */}
                  {qr.usedByUsers && qr.usedByUsers.length > 0 && (
                    <Accordion sx={{ mt: 2 }} disableGutters elevation={0}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">
                          已使用用戶 ({qr.usedByUsers.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <List dense>
                          {qr.usedByUsers.map((usage, idx) => (
                            <ListItem key={idx}>
                              <ListItemText
                                primary={
                                  <Tooltip title={usage.userId}>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                      {usage.userId}
                                    </Typography>
                                  </Tooltip>
                                }
                                secondary={format(usage.usedAt, 'MM/dd HH:mm', { locale: zhTW })}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteClick(qr.id)}
                  >
                    刪除
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          )
        })}
      </Grid>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>產生新的 QR Code</DialogTitle>
        <DialogContent>
          {newCode ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <QRCodeSVG
                value={JSON.stringify({ code: newCode, type: 'registration' })}
                size={200}
              />
              <Typography
                variant="h5"
                sx={{ mt: 2, fontFamily: 'monospace', letterSpacing: 3 }}
              >
                {newCode}
              </Typography>
              <Button
                startIcon={<ContentCopyIcon />}
                onClick={() => handleCopy(newCode)}
                sx={{ mt: 1 }}
              >
                複製代碼
              </Button>
            </Box>
          ) : (
            <Box sx={{ pt: 2 }}>
              <TextField
                label="有效時間 (小時)"
                type="number"
                fullWidth
                value={expiresInHours}
                onChange={(e) => setExpiresInHours(Number(e.target.value))}
                margin="normal"
                inputProps={{ min: 1, max: 168 }}
              />
              <TextField
                label="最大使用次數"
                type="number"
                fullWidth
                value={maxUses}
                onChange={(e) => setMaxUses(Number(e.target.value))}
                margin="normal"
                inputProps={{ min: 1, max: 100 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {newCode ? '關閉' : '取消'}
          </Button>
          {!newCode && (
            <Button variant="contained" onClick={handleCreate} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : '產生'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <Typography>
            確定要刪除此 QR Code 嗎？此操作無法復原。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : '刪除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
